import React, { useEffect, useRef, useState, useCallback } from 'react';
import { fetchEmpresasConfianzaPublic, EmpresaConfianzaPublic } from '../../services/landing/empresaConfianzaPublicService';
import { fetchPublicaciones, Publicacion } from '../../services/landing/blogService';
import { fetchBannerSlidesPublic, BannerSlide } from '../../services/landing/bannerSlideService';
import {
    fetchEspaciosPublic,
    fetchExperienciaPublic,
    fetchSplitSectionsPublic,
    LandingSplitSectionItem,
} from '../../services/landing/landingSectionsService';
import BrandStripes from '../../components/common/BrandStripes';
import CotizacionModal from '../../components/CotizacionModal/CotizacionModal';
import BrandIntroAnimation from '../../components/common/BrandIntroAnimation';
import AstonMartinHero, { HeroSlideItem } from '../../components/HeroBanner/AstonMartinHero';
import coworkImg from '../../assets/images/cowork/banner.jpg';
import cowork2Img from '../../assets/images/cowork/cowork.jpg';
import reuniones2Img from '../../assets/images/cowork/reuniones.jpeg';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';
import BorderGlow from '../../components/common/BorderGlow';
import { useTheme } from '../../context/ThemeContext';
import {
    RiMapPinLine as MapPin,
    RiMailLine as Mail,
    RiWhatsappLine as Whatsapp,
    RiTimeLine as Time,
} from '@remixicon/react';
import SocialIcons from '../../components/common/SocialIcons';

// ═══ IMÁGENES DEL CARRUSEL ═══
import aniosImg from '../../assets/images/cowork/reuniones.jpeg';
import visionImg from '../../assets/images/cowork/reuniones.jpeg';
// ═══ IMÁGENES PARA SPLIT SECTIONS ═══
import espacioImg from '../../assets/images/cowork/reuniones.jpeg';
import solucionImg from '../../assets/images/cowork/reuniones.jpeg';
import AccordionGallery from '../../components/AccordionGallery/AccordionGallery';
import DepthCarousel from '../../components/common/DepthCarousel';

const realPhoto = (name: string) => `${process.env.PUBLIC_URL}/images/comarca/${name}.webp`;
const ABOUT_SPACES = [
    { image: realPhoto('cowork-colaborativo'), label: 'Coworking', alt: 'Área colaborativa real de Co Marca en La Paz' },
    { image: realPhoto('sala-reuniones'), label: 'Sala de reuniones', alt: 'Mesa y sillas de la sala de reuniones de Co Marca' },
    { image: realPhoto('area-lounge'), label: 'Sala de Psicología', alt: 'Sala de Psicología de Co Marca' },
    { image: realPhoto('sala-formacion'), label: 'Sala Taller', alt: 'Sala Taller de Co Marca' },
    { image: realPhoto('oficina-privada'), label: 'Oficina privada', alt: 'Oficina privada de Co Marca' },
];
const EXPERIENCE_COPY = '¡Tu trabajo impulsa el trabajo de otros!\n8 años creando espacios para trabajar, conectar y crecer.\n\nDesde 2018 construimos una comunidad donde cada coworker es parte de algo más grande, su consumo contribuye al desarrollo de nuevos emprendimientos a través de Co Marca Lab.\n\nPorque cuando crecemos juntos, también ayudamos a que nuevas ideas despeguen.';
const HERO_INTRO = 'Un ecosistema emprendedor y colaborativo con propósito. Espacios de trabajo y salas ejecutivas diseñados para potenciar tus proyectos.';
const heroDescription = (description: string, title: string) => /co\s*marca/i.test(title)
    ? HERO_INTRO : description.replace(/Calacoto,?\s*/gi, '');

const colors = {
    primary: '#61257b',
    primaryDark: '#451759',
    secondary: '#c1348b',
    secondaryLight: '#ffd97c',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

// Fix React 18: useRef<T>(null) retorna RefObject<T | null>, se debe reflejar en la firma
function useInView(threshold: number = 0.15): [React.RefObject<HTMLElement | null>, boolean] {
    const ref = useRef<HTMLElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

function AnimatedCounter({ end, label, suffix = '', color = '#61257B' }: { end: number; label: string; suffix?: string; color?: string }) {
    const [count, setCount] = useState(0);
    const [ref, inView] = useInView(0.3);
    useEffect(() => {
        if (!inView) return;
        let start = 0;
        const step = Math.ceil(end / (1800 / 16));
        const timer = setInterval(() => {
            start += step;
            if (start >= end) { setCount(end); clearInterval(timer); }
            else setCount(start);
        }, 16);
        return () => clearInterval(timer);
    }, [inView, end]);
    return (
        <div ref={ref as React.RefObject<HTMLDivElement | null>} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.8rem', fontWeight: '800', color: color, lineHeight: 1 }}>
                {count}{suffix}
            </div>
            <div style={{ color: '#64748b', fontWeight: '500', marginTop: '0.4rem', fontSize: '0.9rem' }}>
                {label}
            </div>
        </div>
    );
}

// ═══ Datos del carrusel ═══
const defaultExperienciaSlides = [
    {
        tag: 'Co Marca Group',
        title: '8 AÑOS DE\nEXPERIENCIA',
        description: EXPERIENCE_COPY,
        accent: '#61257B',
        bgColor: 'linear-gradient(145deg, #451759 0%, #61257B 60%, #C1348B 100%)',
        icon: '',
        stat: '8+',
        statLabel: 'Años',
        image: realPhoto('cowork-colaborativo'),
    },
    {
        tag: 'Co Marca Group',
        title: '+120 PROYECTOS\nREALIZADOS',
        description: 'Más de 120 proyectos nacidos en nuestras salas de reuniones, escritorios y espacios de coworking. Hemos sido el punto de partida de startups, agencias creativas, consultoras y negocios digitales que hoy son referentes en Bolivia y la región.',
        accent: colors.secondary,
        bgColor: `linear-gradient(145deg, ${colors.primaryDark} 0%, ${colors.secondary} 60%, ${colors.secondaryLight} 100%)`,
        icon: '',
        stat: '120+',
        statLabel: 'Proyectos',
        image: aniosImg,
    },
    {
        tag: 'Co Marca Group',
        title: 'NUESTRA\nASPIRACIÓN',
        description: 'Convertir a Co Marca en el hub de innovación más importante de Bolivia: un lugar donde cada persona que entre salga con nuevas conexiones, nuevas ideas y nuevas oportunidades. Queremos ser el catalizador del crecimiento empresarial en toda la región andina.',
        accent: '#61257B',
        bgColor: 'linear-gradient(145deg, #451759 0%, #61257B 60%, #C1348B 100%)',
        icon: '',
        stat: '∞',
        statLabel: 'Visión',
        image: visionImg,
    },
];

// ═══ Componente Carrusel ═══
function CarouselSection({ customSlides }: { customSlides?: any[] }) {
    const { isDark } = useTheme();
    const activeSlides = customSlides && customSlides.length > 0 ? customSlides : defaultExperienciaSlides;
    const [current, setCurrent] = useState(0);
    const [animating, setAnimating] = useState(false);
    const [textVisible, setTextVisible] = useState(true);
    const [sectionRef, sectionInView] = useInView(0.15);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const goTo = useCallback((idx: number) => {
        if (animating) return;
        setAnimating(true);
        setTextVisible(false);
        setTimeout(() => {
            setCurrent(idx);
            setTextVisible(true);
            setAnimating(false);
        }, 420);
    }, [animating]);

    const next = useCallback(() => goTo((current + 1) % activeSlides.length), [current, goTo, activeSlides.length]);
    const prev = useCallback(() => goTo((current - 1 + activeSlides.length) % activeSlides.length), [current, goTo, activeSlides.length]);

    useEffect(() => {
        timerRef.current = setTimeout(next, 5000);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [current, next]);

    const slide = activeSlides[current] || activeSlides[0];

    return (
        <section
            ref={sectionRef as React.RefObject<HTMLDivElement | null>}
            style={{
                background: isDark ? '#0b0614' : '#fff',
                padding: '24px 32px 32px',
                position: 'relative',
                overflow: 'hidden',
                opacity: sectionInView ? 1 : 0,
                transform: sectionInView ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.8s ease, transform 0.8s cubic-bezier(.16,1,.3,1)',
            }}
        >
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{
                    display: 'flex', gap: '72px', alignItems: 'center',
                    flexWrap: 'wrap',
                }}>
                    {/* LEFT: Texto */}
                    <div style={{
                        flex: '1', minWidth: '300px', maxWidth: '500px',
                        opacity: textVisible ? 1 : 0,
                        transform: textVisible ? 'translateX(0)' : 'translateX(-24px)',
                        transition: 'opacity 0.45s ease, transform 0.45s cubic-bezier(.16,1,.3,1)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                            <span style={{
                                fontSize: '0.75rem', fontWeight: '700',
                                letterSpacing: '0.14em', textTransform: 'uppercase',
                                color: slide.accent,
                            }}>
                                {slide.tag}
                            </span>
                            <div style={{
                                height: '2px', width: '48px', borderRadius: '2px',
                                background: slide.accent,
                            }} />
                        </div>
                        <h2 style={{
                            fontSize: '2.8rem', fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a',
                            lineHeight: '1.08', letterSpacing: '-0.02em',
                            marginBottom: '28px', whiteSpace: 'pre-line',
                        }}>
                            {slide.title}
                        </h2>
                        <p style={{
                            fontSize: '1rem', color: isDark ? '#cbd5e1' : '#475569',
                            lineHeight: '1.85', marginBottom: '24px',
                            maxWidth: '460px', whiteSpace: 'pre-line',
                        }}>
                            {slide.description}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <button
                                onClick={prev}
                                style={{
                                    width: '48px', height: '48px', borderRadius: '50%',
                                    border: `2px solid ${slide.accent}40`,
                                    background: isDark ? '#150d24' : '#fff', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '1.1rem', transition: 'all 0.2s',
                                    color: slide.accent,
                                }}
                                onMouseEnter={e => {
                                    (e.currentTarget as HTMLButtonElement).style.background = slide.accent;
                                    (e.currentTarget as HTMLButtonElement).style.color = '#fff';
                                }}
                                onMouseLeave={e => {
                                    (e.currentTarget as HTMLButtonElement).style.background = isDark ? '#150d24' : '#fff';
                                    (e.currentTarget as HTMLButtonElement).style.color = slide.accent;
                                }}
                            >
                                ←
                            </button>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {activeSlides.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => goTo(i)}
                                        style={{
                                            width: i === current ? '28px' : '8px',
                                            height: '8px',
                                            borderRadius: '100px',
                                            border: 'none',
                                            background: i === current ? slide.accent : '#cbd5e1',
                                            cursor: 'pointer',
                                            padding: 0,
                                            transition: 'all 0.35s cubic-bezier(.16,1,.3,1)',
                                        }}
                                    />
                                ))}
                            </div>
                            <button
                                onClick={next}
                                style={{
                                    width: '48px', height: '48px', borderRadius: '50%',
                                    border: 'none',
                                    background: `linear-gradient(135deg, ${slide.accent}, ${slide.accent}cc)`,
                                    cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '1.1rem', color: '#fff',
                                    boxShadow: `0 4px 16px ${slide.accent}40`,
                                    transition: 'all 0.2s',
                                }}
                                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.1)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
                            >
                                →
                            </button>
                            <span style={{ color: '#94a3b8', fontSize: '0.88rem', fontWeight: '600', marginLeft: '4px' }}>
                                {String(current + 1).padStart(2, '0')} / {String(activeSlides.length).padStart(2, '0')}
                            </span>
                        </div>
                    </div>
                    {/* RIGHT: DepthCarousel 3D interactivo con bucle */}
                    <div style={{
                        flex: '1.2', minWidth: '320px', maxWidth: '600px',
                        height: '470px',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <DepthCarousel
                            items={activeSlides.map(s => ({
                                image: s.image,
                                alt: s.title.replace('\n', ' '),
                                stat: s.stat,
                                statLabel: s.statLabel,
                            }))}
                            activeIndex={current}
                            depth={180}
                            spread={80}
                            tilt={18}
                            tiltDirection="right"
                            perspective={1300}
                            visibleCards={3}
                            falloff={0.22}
                            blur={5}
                            cardWidth={320}
                            cardHeight={390}
                            radius={22}
                            tint="#3b0f50"
                            autoplay={true}
                            autoplayDelay={3600}
                            loop={true}
                            showControls={false}
                            showIndicators={false}
                            onChange={(idx) => {
                                setCurrent(idx);
                            }}
                        />
                    </div>
                </div>

                {/* Brand Stripes with Scroll Incentive */}
                <div style={{ marginTop: '36px', display: 'flex', justifyContent: 'center' }}>
                    <BrandStripes variant="scroll-prompt" size="lg" promptText="Desliza para explorar" />
                </div>
            </div>
        </section>
    );
}

// ═══ ClientsSection ═══
const CLIENT_COLORS = [
    '#61257B', '#C1348B', '#8E288F', '#451759',
    '#61257B', '#C1348B', '#8E288F', '#61257B',
];

function ClientsSection() {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    const [empresas, setEmpresas] = useState<EmpresaConfianzaPublic[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchEmpresasConfianzaPublic()
            .then(data => {
                const sorted = [...data].sort((a, b) => a.orden - b.orden);
                setEmpresas(sorted);
            })
            .catch((() => undefined))
            .finally(() => setLoading(false));
    }, []);

    const getInitials = (nombre: string) =>
        nombre.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

    // Replicar lista para garantizar un flujo continuo e infinito sin vacíos
    const streamItems = React.useMemo(() => {
        if (!empresas.length) return [];
        let list = [...empresas];
        while (list.length < 8) {
            list = [...list, ...empresas];
        }
        return list;
    }, [empresas]);

    return (
        <section
            ref={ref as React.RefObject<HTMLDivElement | null>}
            className={`fade-up-anim ${inView ? 'in' : ''}`}
            style={{
                background: isDark ? '#0b0614' : '#fff',
                padding: '48px 0 76px',
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            <style>{`
                @keyframes continuousStreamScroll {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                .flowing-clients-track {
                    display: flex;
                    width: max-content;
                    animation: continuousStreamScroll 38s linear infinite;
                    will-change: transform;
                }
                .flowing-clients-track:hover {
                    animation-play-state: paused;
                }
                .client-pod {
                    transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.35s ease, border-color 0.35s ease;
                }
                .client-pod:hover {
                    transform: translateY(-5px) scale(1.03);
                    border-color: rgba(193, 52, 139, 0.6) !important;
                    box-shadow: 0 16px 36px rgba(193, 52, 139, 0.28) !important;
                }
            `}</style>

            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '38px', flexWrap: 'wrap', gap: '16px' }}>
                    <div style={{ maxWidth: '600px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.primary }}>
                                Ecosistema Empresarial
                            </span>
                            <div style={{ height: '2px', width: '40px', background: colors.primary, borderRadius: '2px' }} />
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.3rem)', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.02em', lineHeight: '1.15', marginBottom: '12px' }}>
                            Las principales empresas <span style={{ color: colors.secondary }}>confían en nosotros</span>
                        </h2>
                        <p style={{ fontSize: '0.97rem', color: isDark ? '#cbd5e1' : '#64748b', lineHeight: '1.7' }}>
                            Compañías que han hecho de Co Marca su sede estratégica para colaborar, proyectar reuniones y escalar sus negocios.
                        </p>
                    </div>
                    <a
                        href="/nuestros-clientes"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            color: isDark ? '#FFD97C' : colors.primary,
                            fontWeight: '700', fontSize: '0.92rem', textDecoration: 'none',
                            border: isDark ? '1.5px solid rgba(193,52,139,0.45)' : `2px solid ${colors.primary}30`,
                            borderRadius: '12px', padding: '8px 20px', transition: 'all 0.2s', whiteSpace: 'nowrap',
                            background: isDark ? 'rgba(97,37,123,0.2)' : 'transparent',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(193,52,139,0.3)' : `${colors.primary}10`; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isDark ? 'rgba(97,37,123,0.2)' : 'transparent'; }}
                    >
                        Ver todas →
                    </a>
                </div>
            </div>

            {/* Continuous Elegant Floating Marquee Container */}
            <div style={{ position: 'relative', width: '100%', overflow: 'hidden', padding: '16px 0' }}>
                {/* Degradados difusos laterales para entrada y salida suave */}
                <div style={{
                    position: 'absolute', top: 0, left: 0, width: 'clamp(60px, 12vw, 180px)', height: '100%',
                    background: `linear-gradient(to right, ${isDark ? '#0b0614' : '#ffffff'} 30%, transparent)`,
                    zIndex: 10, pointerEvents: 'none',
                }} />
                <div style={{
                    position: 'absolute', top: 0, right: 0, width: 'clamp(60px, 12vw, 180px)', height: '100%',
                    background: `linear-gradient(to left, ${isDark ? '#0b0614' : '#ffffff'} 30%, transparent)`,
                    zIndex: 10, pointerEvents: 'none',
                }} />

                {loading ? (
                    <div style={{ display: 'flex', gap: '20px', padding: '0 32px' }}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} style={{ width: '260px', height: '76px', borderRadius: '9999px', background: isDark ? '#150d24' : '#f1f5f9', animation: 'pulse 1.5s ease-in-out infinite' }} />
                        ))}
                    </div>
                ) : streamItems.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '36px', color: isDark ? '#cbd5e1' : '#64748b' }}>
                        No hay empresas registradas por el momento.
                    </div>
                ) : (
                    <div className="flowing-clients-track">
                        {[...streamItems, ...streamItems].map((empresa, i) => {
                            const accentColor = CLIENT_COLORS[i % CLIENT_COLORS.length];
                            return (
                                <div
                                    key={`${empresa.id}-${i}`}
                                    className="client-pod"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '16px',
                                        padding: '12px 26px 12px 16px',
                                        marginRight: '22px',
                                        borderRadius: '9999px',
                                        background: isDark
                                            ? 'linear-gradient(135deg, rgba(25, 14, 43, 0.85) 0%, rgba(15, 8, 26, 0.95) 100%)'
                                            : 'linear-gradient(135deg, #ffffff 0%, #fdf8ff 100%)',
                                        backdropFilter: 'blur(16px)',
                                        border: isDark
                                            ? '1.5px solid rgba(193, 52, 139, 0.25)'
                                            : '1.5px solid rgba(97, 37, 123, 0.14)',
                                        boxShadow: isDark
                                            ? '0 8px 24px rgba(0, 0, 0, 0.35)'
                                            : '0 8px 20px rgba(97, 37, 123, 0.05)',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                    }}
                                >
                                    {empresa.logo ? (
                                        <div style={{
                                            width: '48px', height: '48px', borderRadius: '50%',
                                            overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc',
                                            border: `1.5px solid ${accentColor}30`,
                                            flexShrink: 0,
                                            padding: '4px',
                                        }}>
                                            <img
                                                src={empresa.logo}
                                                alt={empresa.logo_alt || empresa.nombre}
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                        </div>
                                    ) : (
                                        <div style={{
                                            width: '48px', height: '48px', borderRadius: '50%',
                                            background: `linear-gradient(135deg, ${accentColor}40, ${accentColor}15)`,
                                            border: `2px solid ${accentColor}40`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontWeight: '800', fontSize: '0.95rem',
                                            color: isDark ? '#ffffff' : accentColor,
                                            flexShrink: 0,
                                        }}>
                                            {getInitials(empresa.nombre)}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{
                                            fontSize: '0.95rem', fontWeight: '800',
                                            color: isDark ? '#ffffff' : '#0f172a',
                                            letterSpacing: '-0.01em', whiteSpace: 'nowrap',
                                        }}>
                                            {empresa.nombre}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                            <span style={{
                                                fontSize: '0.7rem', fontWeight: '700',
                                                color: accentColor,
                                                letterSpacing: '0.04em', textTransform: 'uppercase',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {empresa.rubro || 'Aliado Estratégico'}
                                            </span>
                                            <span style={{
                                                width: '4px', height: '4px', borderRadius: '50%',
                                                backgroundColor: isDark ? '#a855f7' : '#c1348b',
                                            }} />
                                            <span style={{
                                                fontSize: '0.68rem', color: isDark ? '#94a3b8' : '#64748b',
                                                fontWeight: '500', whiteSpace: 'nowrap',
                                            }}>
                                                Miembro
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div style={{ maxWidth: '1200px', margin: '40px auto 0', padding: '0 32px', display: 'flex', justifyContent: 'center' }}>
                <BrandStripes size="md" />
            </div>
        </section>
    );
}

// ═══ SplitSection (modificada para aceptar imagen) ═══
interface SplitSectionProps {
    imageLeft: boolean;
    tag: string;
    title: string;
    description: string;
    features: string[];
    accent: string;
    bg: string;
    icon: string;
    href: string;
    buttonText?: string;
    imageSrc?: string; // Nueva prop para imagen de fondo
}

function SplitSection({ imageLeft, tag, title, description, features, accent, bg, icon, href, buttonText, imageSrc }: SplitSectionProps) {
    const { isDark } = useTheme();
    const [leftRef] = useInView(0.1);
    const [rightRef] = useInView(0.1);

    const textBlock = (
        <div style={{ flex: '1', minWidth: '280px', maxWidth: '520px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: accent }}>
                    {tag}
                </span>
                <div style={{ height: '2px', width: '40px', borderRadius: '2px', background: accent }} />
            </div>
            <h2 style={{ fontSize: 'clamp(1.7rem, 3vw, 2.4rem)', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.15', letterSpacing: '-0.02em', marginBottom: '20px' }}>
                {title}
            </h2>
            <p style={{ fontSize: '1rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.85', marginBottom: '28px' }}>
                {description}
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {features.map((f: string) => (
                    <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.93rem', color: isDark ? '#e2e8f0' : '#374151', fontWeight: '500' }}>
                        <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: accent, display: 'block' }} />
                        </span>
                        {f}
                    </li>
                ))}
            </ul>
            <a
                href={href}
                className="info-btn"
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
                    color: '#fff', borderRadius: '12px', padding: '12px 28px',
                    fontWeight: '700', fontSize: '0.92rem', textDecoration: 'none',
                    boxShadow: `0 6px 20px ${accent}35`, letterSpacing: '0.01em',
                }}
            >
                {buttonText || 'Obtener más información →'}
            </a>
        </div>
    );

    const imgBlock = (
        <div
            ref={(imageLeft ? leftRef : rightRef) as React.RefObject<HTMLDivElement | null>}
            className="split-img"
            style={{ flex: '1', minWidth: '280px', maxWidth: '540px', minHeight: '400px', borderRadius: '28px', position: 'relative', overflow: 'hidden' }}
        >
            <div style={{
                width: '100%', height: '100%', minHeight: '400px',
                backgroundImage: imageSrc ? `url(${imageSrc})` : `linear-gradient(145deg, ${accent}dd, ${accent}88)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: '28px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column', gap: '16px',
                boxShadow: `0 24px 64px ${accent}28`,
                position: 'relative', overflow: 'hidden',
            }}>
                {imageSrc && (
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(0,0,0,0.3)',
                        zIndex: 1,
                    }} />
                )}
                <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '220px', height: '220px', background: 'rgba(255,255,255,0.08)', borderRadius: '50%', zIndex: 2 }} />
                <div style={{ position: 'absolute', bottom: '-60px', left: '-40px', width: '260px', height: '260px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', zIndex: 2 }} />
                <div style={{ fontSize: '5rem', filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.15))', zIndex: 2 }}>{icon}</div>
                <div style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(12px)', borderRadius: '14px', padding: '12px 24px', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', fontWeight: '700', fontSize: '0.95rem', zIndex: 2 }}>
                    Co Marca Group
                </div>
            </div>
        </div>
    );

    const [sectionRef, sectionInView] = useInView(0.08);

    return (
        <section ref={sectionRef as React.RefObject<HTMLDivElement | null>} className={`fade-up-anim ${sectionInView ? 'in' : ''} split-section`} style={{ background: isDark ? (bg === '#fff' ? '#0b0614' : '#0e071a') : bg, padding: '100px 32px', overflow: 'hidden' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div className="split-layout" style={{ display: 'flex', gap: '72px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {imageLeft ? <>{imgBlock}{textBlock}</> : <>{textBlock}{imgBlock}</>}
                </div>
            </div>
        </section>
    );
}

// ═══ ContactSection ═══
type ContactFormState = {
    nombre: string;
    email: string;
    empresa: string;
    telefono: string;
    ciudad: string;
    personas: string;
    mensaje: string;
};

function ContactSection() {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    const [form, setForm] = React.useState<ContactFormState>({
        nombre: '',
        email: '',
        empresa: '',
        telefono: '',
        ciudad: '',
        personas: '',
        mensaje: '',
    });
    const [sent, setSent] = React.useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSent(true);
        setTimeout(() => setSent(false), 4000);
        setForm({
            nombre: '',
            email: '',
            empresa: '',
            telefono: '',
            ciudad: '',
            personas: '',
            mensaje: '',
        });
    };

    const handleCopy = (text: string, field: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const contactItems = [
        {
            id: 'ubicacion',
            num: '01',
            icon: <MapPin size={22} />,
            label: 'Ubicación',
            value: 'Calle 23 de Calacoto, La Paz, Bolivia',
            color: '#C1348B',
            actionText: 'Maps ↗',
            actionUrl: 'https://maps.google.com/?q=Calle+23+de+Calacoto,+La+Paz,+Bolivia',
            isExternal: true,
        },
        {
            id: 'correo',
            num: '02',
            icon: <Mail size={22} />,
            label: 'Correo Electrónico',
            value: 'comarca@gmail.com',
            color: '#61257B',
            actionText: 'Copiar',
            canCopy: true,
        },
        {
            id: 'whatsapp',
            num: '03',
            icon: <Whatsapp size={22} />,
            label: 'WhatsApp Oficial',
            value: '+591 762 04601',
            color: '#EA8406',
            actionText: 'Chatear ↗',
            actionUrl: 'https://api.whatsapp.com/send?phone=59176204601&text=Hola%20Co%20Marca,%20quisiera%20m%C3%A1s%20informaci%C3%B3n',
            isExternal: true,
        },
        {
            id: 'horario',
            num: '04',
            icon: <Time size={22} />,
            label: 'Horario de Atención',
            value: 'Lun – Vie: 8:30 – 19:30 | Sáb: 9:00 – 14:00',
            color: '#C1348B',
            actionText: 'Activo',
            isBadge: true,
        },
    ];

    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '11px 16px',
        border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e5e7eb',
        borderRadius: '12px',
        fontSize: '0.93rem',
        color: isDark ? '#f8fafc' : '#374151',
        fontFamily: "'DM Sans', sans-serif",
        outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
        background: isDark ? '#190e2b' : '#fff',
    };

    return (
        <section ref={ref as React.RefObject<HTMLDivElement | null>} className={`fade-up-anim ${inView ? 'in' : ''} contact-section`} style={{ background: isDark ? '#0b0614' : '#f8fafc', padding: '100px 32px', position: 'relative', overflow: 'hidden', transition: 'background-color 0.3s ease' }}>
            <style>{`
                .contact-input:focus {
                    border-color: #61257B !important;
                    box-shadow: 0 0 0 3px rgba(97,37,123,0.15) !important;
                }
                .contact-input::placeholder { color: #cbd5e1; }
                @media (max-width: 900px) {
                    .landing-contact-grid {
                        flex-direction: column !important;
                        gap: 28px !important;
                        align-items: stretch !important;
                    }
                    .landing-contact-info-col {
                        flex: 1 1 auto !important;
                        width: 100% !important;
                        min-width: 0 !important;
                    }
                    .landing-contact-form-card {
                        width: 100% !important;
                        min-width: 0 !important;
                        padding: clamp(24px, 4.5vw, 36px) clamp(16px, 3.5vw, 28px) !important;
                    }
                }
                @media (max-width: 600px) {
                    .landing-contact-info-header {
                        padding: 22px 16px 18px !important;
                    }
                    .landing-contact-info-body {
                        padding: 16px 12px 18px !important;
                    }
                    .landing-emil-card {
                        padding: 12px 10px 12px 12px !important;
                        gap: 10px !important;
                    }
                    .landing-emil-icon {
                        width: 36px !important;
                        height: 36px !important;
                    }
                    .landing-emil-val {
                        font-size: 0.82rem !important;
                    }
                }
            `}</style>
            <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '380px', height: '380px', background: `radial-gradient(circle, ${colors.primary}10 0%, transparent 70%)`, pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '300px', height: '300px', background: `radial-gradient(circle, ${colors.secondary}08 0%, transparent 70%)`, pointerEvents: 'none' }} />

            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div
                    className="landing-contact-grid"
                    style={{
                        display: 'flex', gap: '28px', alignItems: 'flex-start',
                    }}
                >
                    {/* ── Info card con BorderGlow ── */}
                    <div className="landing-contact-info-col" style={{ flex: '0 0 430px', minWidth: 0, maxWidth: '100%' }}>
                        <BorderGlow
                            borderRadius={28}
                            backgroundColor={isDark ? '#150d24' : '#ffffff'}
                        >
                            <div style={{
                                width: '100%',
                                background: isDark ? '#150d24' : '#ffffff',
                                borderRadius: '28px',
                                overflow: 'hidden',
                            }}>
                                {/* Header de la card */}
                                <div className="landing-contact-info-header" style={{
                                    background: 'linear-gradient(135deg, #420f54 0%, #61257B 50%, #C1348B 100%)',
                                    padding: '30px 28px 26px',
                                    position: 'relative', overflow: 'hidden',
                                }}>
                                    <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '160px', height: '160px', background: 'rgba(255,255,255,0.08)', borderRadius: '50%' }} />
                                    <div style={{ position: 'absolute', bottom: '-50px', left: '-20px', width: '180px', height: '180px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
                                    <div style={{ position: 'relative' }}>
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '7px',
                                            background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(10px)',
                                            borderRadius: '20px', padding: '4px 12px',
                                            fontSize: '0.68rem', fontWeight: '800', color: '#fff',
                                            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                        }}>
                                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#8b3fa8', boxShadow: '0 0 8px #8b3fa8' }} />
                                            Canales Directos Co Marca
                                        </div>
                                        <h2 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#fff', lineHeight: '1.25', marginBottom: '6px' }}>
                                            Información de Contacto
                                        </h2>
                                        <p style={{ fontSize: '0.86rem', color: 'rgba(255,255,255,0.85)', lineHeight: '1.55' }}>
                                            Conéctate con nosotros por cualquiera de nuestros canales oficiales o visítanos en Calacoto.
                                        </p>
                                    </div>
                                </div>

                                {/* Items de contacto individuales */}
                                <div className="landing-contact-info-body" style={{ padding: '24px 22px 28px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {contactItems.map((item) => (
                                            <div
                                                key={item.id}
                                                className="landing-emil-card"
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '14px',
                                                    padding: '16px 16px 16px 18px',
                                                    background: isDark ? '#1b102e' : '#ffffff',
                                                    borderRadius: '18px',
                                                    border: isDark ? '1.5px solid rgba(193,52,139,0.22)' : '1.5px solid #edf2f7',
                                                    boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.03)',
                                                    position: 'relative',
                                                    cursor: item.actionUrl ? 'pointer' : 'default',
                                                    overflow: 'hidden',
                                                    transition: 'all 0.25s',
                                                }}
                                                onClick={() => {
                                                    if (item.actionUrl) {
                                                        window.open(item.actionUrl, item.isExternal ? '_blank' : '_self');
                                                    }
                                                }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                                    e.currentTarget.style.borderColor = `${item.color}60`;
                                                    e.currentTarget.style.boxShadow = `0 12px 24px -6px ${item.color}22, 0 2px 6px rgba(0,0,0,0.04)`;
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.borderColor = isDark ? 'rgba(193,52,139,0.22)' : '#edf2f7';
                                                    e.currentTarget.style.boxShadow = isDark ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.03)';
                                                }}
                                            >
                                                {/* Número */}
                                                <span style={{
                                                    position: 'absolute', top: '7px', left: '10px',
                                                    fontSize: '0.62rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                    color: isDark ? '#71717a' : '#94a3b8', fontWeight: '700', letterSpacing: '0.04em',
                                                }}>
                                                    {item.num}
                                                </span>

                                                {/* Icono */}
                                                <div className="landing-emil-icon" style={{
                                                    width: '42px', height: '42px', borderRadius: '12px', flexShrink: 0,
                                                    background: `${item.color}18`,
                                                    border: `1.5px solid ${item.color}35`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: item.color,
                                                    marginTop: '4px',
                                                }}>
                                                    {item.icon}
                                                </div>

                                                {/* Información */}
                                                <div style={{ flex: 1, minWidth: 0, marginTop: '4px' }}>
                                                    <div style={{
                                                        fontSize: '0.7rem', fontWeight: '800', color: item.color,
                                                        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px',
                                                    }}>
                                                        {item.label}
                                                    </div>
                                                    <div className="landing-emil-val" style={{
                                                        fontSize: 'clamp(0.82rem, 3.2vw, 0.92rem)', fontWeight: '600', color: isDark ? '#ffffff' : '#0f172a',
                                                        wordBreak: 'break-word', overflowWrap: 'anywhere',
                                                    }}>
                                                        {item.value}
                                                    </div>
                                                </div>

                                                {/* Action pill / Copiar badge */}
                                                {item.canCopy ? (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleCopy(item.value, item.id, e)}
                                                        style={{
                                                            flexShrink: 0,
                                                            fontSize: '0.72rem', fontWeight: '700',
                                                            color: copiedField === item.id ? '#10b981' : item.color,
                                                            background: copiedField === item.id ? (isDark ? 'rgba(16,185,129,0.2)' : '#ecfdf5') : `${item.color}15`,
                                                            border: `1px solid ${copiedField === item.id ? '#10b981' : item.color + '40'}`,
                                                            borderRadius: '8px', padding: '5px 10px',
                                                            cursor: 'pointer', transition: 'all 0.2s',
                                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                            marginTop: '4px',
                                                        }}
                                                        title="Copiar al portapapeles"
                                                    >
                                                        {copiedField === item.id ? '✓ Copiado' : 'Copiar ⎘'}
                                                    </button>
                                                ) : item.isBadge ? (
                                                    <span
                                                        style={{
                                                            flexShrink: 0,
                                                            fontSize: '0.68rem', fontWeight: '700',
                                                            color: '#10b981', background: isDark ? 'rgba(16,185,129,0.2)' : '#ecfdf5',
                                                            border: '1px solid rgba(16,185,129,0.4)',
                                                            borderRadius: '8px', padding: '4px 8px',
                                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                            marginTop: '4px',
                                                        }}
                                                    >
                                                        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#10b981' }} />
                                                        Activo
                                                    </span>
                                                ) : (
                                                    <span
                                                        style={{
                                                            flexShrink: 0,
                                                            fontSize: '0.72rem', fontWeight: '700',
                                                            color: item.color, background: `${item.color}15`,
                                                            border: `1px solid ${item.color}35`,
                                                            borderRadius: '8px', padding: '5px 9px',
                                                            display: 'inline-flex', alignItems: 'center', gap: '2px',
                                                            marginTop: '4px',
                                                        }}
                                                    >
                                                        {item.actionText}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Redes sociales */}
                                    <div style={{ marginTop: '26px', paddingTop: '22px', borderTop: isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f1f5f9' }}>
                                        <div style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            marginBottom: '26px',
                                        }}>
                                            <span style={{
                                                fontSize: '0.72rem', fontWeight: '800', color: isDark ? '#cbd5e1' : '#64748b',
                                                textTransform: 'uppercase', letterSpacing: '0.08em',
                                            }}>
                                                Síguenos en Redes
                                            </span>
                                            <span style={{
                                                fontSize: '0.66rem', fontWeight: '700', color: isDark ? '#a1a1aa' : '#94a3b8',
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                            }}>
                                                04 CANALES OFICIALES
                                            </span>
                                        </div>

                                        <div style={{ padding: '10px 0 6px' }}>
                                            <SocialIcons buttonSize={52} iconSize={26} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </BorderGlow>
                    </div>

                    {/* ── Formulario con BorderGlow ── */}
                    <div style={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
                        <BorderGlow
                            borderRadius={28}
                            backgroundColor={isDark ? '#150d24' : '#ffffff'}
                        >
                            <div className="landing-contact-form-card" style={{
                                width: '100%',
                                background: isDark ? '#150d24' : '#fff',
                                borderRadius: '28px',
                                padding: 'clamp(28px, 4.5vw, 40px) clamp(18px, 4vw, 36px)',
                            }}>
                                {/* Label + línea */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: isDark ? '#FFD97C' : '#61257B' }}>
                                        Formulario
                                    </span>
                                    <div style={{ height: '2px', width: '32px', borderRadius: '2px', background: isDark ? '#FFD97C' : '#61257B' }} />
                                </div>
                                <h2 style={{ fontSize: '1.6rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.2', letterSpacing: '-0.02em', marginBottom: '28px' }}>
                                    Envíanos un <span style={{ color: isDark ? '#FFD97C' : '#61257B' }}>mensaje</span>
                                </h2>

                                {sent ? (
                                    <div style={{ textAlign: 'center', padding: '48px 0' }}>
                                        <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>✅</div>
                                        <div style={{ fontSize: '1.2rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '8px' }}>¡Mensaje enviado!</div>
                                        <div style={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.95rem' }}>Nuestro equipo se contactará contigo pronto.</div>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                                        {/* Nombre completo + Correo electrónico */}
                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    Nombre completo <span style={{ color: '#C1348B' }}>*</span>
                                                </label>
                                                <input
                                                    type="text" required
                                                    className="contact-input"
                                                    placeholder="Tu nombre"
                                                    value={form.nombre}
                                                    onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    Correo electrónico <span style={{ color: '#C1348B' }}>*</span>
                                                </label>
                                                <input
                                                    type="email" required
                                                    className="contact-input"
                                                    placeholder="tu@email.com"
                                                    value={form.email}
                                                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </div>

                                        {/* Nombre de la empresa + Número de teléfono */}
                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    Nombre de la empresa
                                                </label>
                                                <input
                                                    type="text"
                                                    className="contact-input"
                                                    placeholder="Empresa S.A."
                                                    value={form.empresa}
                                                    onChange={e => setForm(p => ({ ...p, empresa: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    Número de teléfono
                                                </label>
                                                <input
                                                    type="tel"
                                                    className="contact-input"
                                                    placeholder="+591 7XX XXXXX"
                                                    value={form.telefono}
                                                    onChange={e => setForm(p => ({ ...p, telefono: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </div>

                                        {/* Ciudad + N° de personas */}
                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    Ciudad
                                                </label>
                                                <input
                                                    type="text"
                                                    className="contact-input"
                                                    placeholder="La Paz"
                                                    value={form.ciudad}
                                                    onChange={e => setForm(p => ({ ...p, ciudad: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    N° de personas
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="contact-input"
                                                    placeholder="1"
                                                    value={form.personas}
                                                    onChange={e => setForm(p => ({ ...p, personas: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </div>

                                        {/* Mensaje */}
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                Mensaje <span style={{ color: '#C1348B' }}>*</span>
                                            </label>
                                            <textarea
                                                required rows={5}
                                                className="contact-input"
                                                placeholder="Cuéntanos en qué podemos ayudarte..."
                                                value={form.mensaje}
                                                onChange={e => setForm(p => ({ ...p, mensaje: e.target.value }))}
                                                style={{ ...inputStyle, resize: 'none' as const }}
                                            />
                                        </div>

                                        <p style={{ fontSize: '0.78rem', color: isDark ? '#94a3b8' : '#64748b', lineHeight: '1.6' }}>
                                            Al hacer clic en el botón, aceptas nuestros{' '}
                                            <a href="/terminos" style={{ color: isDark ? '#FFD97C' : '#61257B', textDecoration: 'none', fontWeight: '600' }}>Términos de servicio</a>
                                            {' '}y nuestra{' '}
                                            <a href="/privacidad" style={{ color: isDark ? '#FFD97C' : '#61257B', textDecoration: 'none', fontWeight: '600' }}>Política de privacidad</a>.
                                        </p>

                                        <button type="submit" style={{
                                            background: 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)',
                                            color: '#fff', border: 'none', borderRadius: '14px',
                                            padding: '16px', fontWeight: '700', fontSize: '1rem',
                                            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                            boxShadow: '0 8px 24px rgba(97,37,123,0.35)',
                                            transition: 'all 0.25s cubic-bezier(.16,1,.3,1)',
                                            letterSpacing: '0.01em', marginTop: '6px',
                                        }}
                                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(193,52,139,0.45)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(97,37,123,0.35)'; }}
                                        >
                                            Enviar mensaje →
                                        </button>
                                    </form>
                                )}
                            </div>
                        </BorderGlow>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ═══ BlogSection ═══
const BLOG_COLORS = [colors.primary, colors.secondary, colors.accentDark];
const BLOG_ICONS = ['🤝', '⚡', '🌱', '🚀', '💡', '📚'];

function BlogSection() {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    const [posts, setPosts] = useState<Publicacion[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPublicaciones({ estado: 'publicado', ordering: '-fecha_publicacion' })
            .then(data => {
                const destacados = data.filter(p => p.es_destacado);
                const normales = data.filter(p => !p.es_destacado);
                const combined = [...destacados, ...normales];
                setPosts(combined.slice(0, 3));
            })
            .catch((() => undefined))
            .finally(() => setLoading(false));
    }, []);

    const formatFecha = (fecha: string | null) => {
        if (!fecha) return '';
        return new Date(fecha).toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
    };

    return (
        <section ref={ref as React.RefObject<HTMLDivElement | null>} className={`fade-up-anim ${inView ? 'in' : ''} blog-section`} style={{ background: isDark ? '#0e071a' : '#fff', padding: '100px 32px 120px', overflow: 'hidden' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '48px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.primary }}>Nuestros Blogs</span>
                            <div style={{ height: '2px', width: '40px', background: colors.primary, borderRadius: '2px' }} />
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.7rem, 3vw, 2.4rem)', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.02em' }}>
                            Artículos e <span style={{ color: colors.secondary }}>inspiración</span>
                        </h2>
                    </div>
                    <a href="/blog"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            color: isDark ? '#FFD97C' : colors.primary,
                            fontWeight: '700', fontSize: '0.92rem', textDecoration: 'none',
                            border: isDark ? '1.5px solid rgba(193,52,139,0.45)' : `2px solid ${colors.primary}30`,
                            borderRadius: '10px', padding: '8px 18px', transition: 'all 0.2s',
                            background: isDark ? 'rgba(97,37,123,0.2)' : 'transparent',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(193,52,139,0.3)' : `${colors.primary}10`; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isDark ? 'rgba(97,37,123,0.2)' : 'transparent'; }}
                    >
                        Ver todos →
                    </a>
                </div>
                {loading && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
                        {[1, 2, 3].map(i => (
                            <div key={i} style={{ background: isDark ? '#150d24' : '#f8fafc', borderRadius: '24px', height: '380px' }} />
                        ))}
                    </div>
                )}
                {!loading && posts.length > 0 && (
                    <div className="blog-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
                        {posts.map((post, i) => {
                            const accentColor = BLOG_COLORS[i % BLOG_COLORS.length];
                            const icon = BLOG_ICONS[i % BLOG_ICONS.length];
                            const tagName = post.categoria?.nombre ?? 'Blog';
                            const imagenUrl = post.url_imagen || post.imagen;
                            return (
                                <div key={post.id} style={{ display: 'flex', height: '100%' }}>
                                    <BorderGlow
                                        borderRadius={24}
                                        backgroundColor="#ffffff"
                                        backgroundColorDark="#150d24"
                                        style={{ width: '100%', height: '100%' }}
                                    >
                                        <article
                                            className="blog-card"
                                            style={{
                                                background: 'transparent',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                height: '100%',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <div style={{
                                                height: '200px',
                                                background: imagenUrl
                                                    ? `url(${imagenUrl}) center/cover no-repeat`
                                                    : `linear-gradient(145deg, ${accentColor}dd, ${accentColor}88)`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                position: 'relative', overflow: 'hidden', flexShrink: 0,
                                            }}>
                                                {!imagenUrl && (
                                                    <>
                                                        <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '130px', height: '130px', background: 'rgba(255,255,255,0.08)', borderRadius: '50%' }} />
                                                        <div style={{ fontSize: '3.5rem', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' }}>{icon}</div>
                                                    </>
                                                )}
                                                {imagenUrl && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 60%)' }} />}
                                                <span style={{ position: 'absolute', top: '16px', left: '16px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '100px', padding: '4px 12px', fontSize: '0.75rem', fontWeight: '700', color: '#fff', letterSpacing: '0.06em' }}>
                                                    {tagName}
                                                </span>
                                                {post.es_destacado && (
                                                    <span style={{ position: 'absolute', top: '16px', right: '16px', background: `${colors.accentDark}`, borderRadius: '100px', padding: '3px 10px', fontSize: '0.7rem', fontWeight: '700', color: '#fff' }}>
                                                        ★ Destacado
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                                                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
                                                    <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: '500' }}>
                                                        {formatFecha(post.fecha_publicacion || post.creado_en)}
                                                    </span>
                                                    <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#cbd5e1' }} />
                                                    <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: '500' }}>{post.nombre_autor}</span>
                                                </div>
                                                <h3 style={{ fontSize: '1.05rem', fontWeight: '700', color: '#0f172a', lineHeight: '1.4', marginBottom: '12px', letterSpacing: '-0.01em' }}>
                                                    {post.titulo}
                                                </h3>
                                                <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: '1.7', marginBottom: '20px' }}>
                                                    {post.subtitulo || post.contenido.replace(/<[^>]+>/g, '').slice(0, 120)}
                                                    {(post.subtitulo || post.contenido.replace(/<[^>]+>/g, '')).length > 120 ? '…' : ''}
                                                </p>
                                                <a href={`/blog/${post.slug}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: accentColor, fontWeight: '700', fontSize: '0.85rem', textDecoration: 'none', transition: 'gap 0.2s', marginTop: 'auto' }}
                                                    onMouseEnter={e => e.currentTarget.style.gap = '10px'}
                                                    onMouseLeave={e => e.currentTarget.style.gap = '6px'}
                                                >
                                                    Leer artículo →
                                                </a>
                                            </div>
                                        </article>
                                    </BorderGlow>
                                </div>
                            );
                        })}
                    </div>
                )}
                {!loading && posts.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
                        No hay publicaciones disponibles por el momento.
                    </div>
                )}
            </div>
        </section>
    );
}

// ═══════════════════════════════════════
// ═══ HERO SLIDES DATA (ASTON MARTIN FULLSCREEN) ═══
const heroSlides: HeroSlideItem[] = [
    {
        id: 'brand',
        type: 'image',
        src: coworkImg,
        title: 'COMARCA',
        titleAccent: 'GROUP',
        accentColor: '#c1348b', // Color de marca original, NO dorado
        description: HERO_INTRO,
        ctaPrimaryText: 'Cotizar algún servicio',
        ctaSecondaryText: 'Hablar con un asesor',
    },
    {
        id: 'video-demo',
        type: 'video',
        src: reuniones2Img,
        videoSrc: `${process.env.PUBLIC_URL}/videos/sample.mp4`,
        title: 'DECISIONES QUE',
        titleAccent: 'TRANSFORMAN NEGOCIOS',
        accentColor: '#c1348b', // Color de marca original, NO dorado
        description: 'Salas equipadas con tecnología de primer nivel para reuniones de alto impacto. El espacio ideal para cerrar acuerdos y construir el futuro de tu empresa.',
        ctaPrimaryText: 'Cotizar algún servicio',
        ctaSecondaryText: 'Hablar con un asesor',
    },
    {
        id: 'cowork-space',
        type: 'image',
        src: cowork2Img,
        title: 'INNOVACIÓN &',
        titleAccent: 'COWORKING',
        accentColor: '#61257B', // Color de marca original, NO dorado
        description: 'Comunidad activa de empresas y profesionales que colaboran y escalan sus proyectos en La Paz.',
        ctaPrimaryText: 'Cotizar algún servicio',
        ctaSecondaryText: 'Hablar con un asesor',
    },
];

const LandingPage = () => {
    const { isDark } = useTheme();
    const [isCotizacionOpen, setIsCotizacionOpen] = useState(false);
    const [aboutRef, aboutInView] = useInView(0.1);
    const [slides, setSlides] = useState<HeroSlideItem[]>(heroSlides);
    const [spacesList, setSpacesList] = useState(ABOUT_SPACES);
    const [experienciaList, setExperienciaList] = useState<any[]>(defaultExperienciaSlides);
    const [splitSectionsList, setSplitSectionsList] = useState<LandingSplitSectionItem[]>([]);

    useEffect(() => {
        let isMounted = true;
        fetchBannerSlidesPublic()
            .then((data: BannerSlide[]) => {
                if (!isMounted || !data || data.length === 0) return;
                const mapped: HeroSlideItem[] = data.map((item, idx) => {
                    let finalSrc = item.url_imagen || item.imagen || '';
                    if (!finalSrc) {
                        if (idx === 0) finalSrc = coworkImg;
                        else if (idx === 1) finalSrc = reuniones2Img;
                        else finalSrc = cowork2Img;
                    }
                    let finalVideoSrc = item.url_video || item.video || item.video_url || '';
                    if (item.tipo === 'video' && !finalVideoSrc) {
                        finalVideoSrc = `${process.env.PUBLIC_URL}/videos/sample.mp4`;
                    }

                    return {
                        id: String(item.id),
                        type: item.tipo,
                        src: finalSrc,
                        videoSrc: finalVideoSrc || undefined,
                        title: item.titulo,
                        titleAccent: item.titulo_acento || '',
                        accentColor: item.color_acento || '#c1348b',
                        description: heroDescription(item.descripcion, item.titulo),
                        ctaPrimaryText: item.texto_boton_primario || 'Cotizar algún servicio',
                        ctaSecondaryText: item.texto_boton_secundario || 'Hablar con un asesor',
                        secondaryUrl: item.enlace_boton_secundario || undefined,
                    };
                });
                setSlides(mapped);
            })
            .catch((err) => {
                /* Browser diagnostics omitted. */
            });

        // Cargar galería de espacios (acordeón)
        fetchEspaciosPublic()
            .then((data) => {
                if (!isMounted || !data || data.length === 0) return;
                const mapped = data.map((e, idx) => {
                    const photoIndex = idx % ABOUT_SPACES.length;
                    const photo = ABOUT_SPACES[photoIndex];
                    // Estos nombres corresponden a las fotos reales, incluso con textos antiguos de la API.
                    const correctedCaption = photoIndex === 2 || photoIndex === 3;
                    return {
                        image: photo.image,
                        label: correctedCaption ? photo.label : e.label,
                        alt: correctedCaption ? photo.alt : e.alt || e.label,
                    };
                });
                setSpacesList(mapped);
            })
            .catch((() => undefined));

        // Cargar carrusel de experiencia / aspiración (3D)
        fetchExperienciaPublic()
            .then((data) => {
                if (!isMounted || !data || data.length === 0) return;
                const mapped = data.map((item, idx) => ({
                    tag: item.tag || 'Co Marca Group',
                    title: idx === 0 ? '8 AÑOS DE\nEXPERIENCIA' : item.titulo,
                    description: idx === 0 ? EXPERIENCE_COPY : item.descripcion,
                    accent: item.color_acento || '#61257B',
                    bgColor: 'linear-gradient(145deg, #451759 0%, #61257B 60%, #C1348B 100%)',
                    icon: '',
                    stat: idx === 0 ? '8+' : item.stat,
                    statLabel: item.stat_label,
                    image: idx === 0 ? realPhoto('cowork-colaborativo') : item.url_imagen || item.imagen || (idx === 1 ? aniosImg : visionImg),
                }));
                setExperienciaList(mapped);
            })
            .catch((() => undefined));

        // Cargar secciones divididas (split sections)
        fetchSplitSectionsPublic()
            .then((data) => {
                if (!isMounted || !data || data.length === 0) return;
                setSplitSectionsList(data);
            })
            .catch((() => undefined));

        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", overflowX: 'clip', background: '#fff' }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400&family=Montserrat:wght@700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        /* ── ASTON MARTIN MINIMALIST CINEMATIC HERO ── */
        .hero-arrow-btn:hover {
          background: rgba(255, 255, 255, 0.28) !important;
          border-color: rgba(255, 255, 255, 0.6) !important;
          transform: translateY(-50%) scale(1.08) !important;
        }
        .hero-arrow-btn:active {
          transform: translateY(-50%) scale(0.96) !important;
        }

        /* Buttons */
        .hero-btn-p { transition: all 0.22s ease; }
        .hero-btn-p:hover { transform: translateY(-3px) scale(1.02); filter: brightness(1.08); box-shadow: 0 16px 36px rgba(193, 52, 139, 0.5) !important; }
        .hero-btn-wa { transition: all 0.22s ease; }
        .hero-btn-wa:hover { transform: translateY(-3px) scale(1.02); background: rgba(255,255,255,0.25) !important; border-color: rgba(255,255,255,0.55) !important; }

        /* Slide dots */
        .slide-dot { transition: all 0.35s cubic-bezier(.16,1,.3,1); }

        /* ── REST OF SITE ANIMATIONS ── */
        .stat-card { transition: all 0.3s; }
        .stat-card:hover { transform: translateY(-6px); box-shadow: 0 20px 48px rgba(0,0,0,0.1) !important; }
        .about-img-card { overflow: hidden; transition: transform 0.4s; }
        .about-img-card:hover { transform: scale(1.03); }
        .about-left-anim  { opacity: 0; transform: translateX(-50px); transition: opacity 0.85s ease, transform 0.85s cubic-bezier(.16,1,.3,1); }
        .about-left-anim.in  { opacity: 1; transform: translateX(0); }
        .about-right-anim { opacity: 0; transform: translateX(50px); transition: opacity 0.85s ease 0.15s, transform 0.85s cubic-bezier(.16,1,.3,1) 0.15s; }
        .about-right-anim.in { opacity: 1; transform: translateX(0); }
        .fade-up-anim  { opacity: 0; transform: translateY(30px); transition: opacity 0.7s ease, transform 0.7s cubic-bezier(.16,1,.3,1); }
        .fade-up-anim.in  { opacity: 1; transform: translateY(0); }
        .split-anim-left  { opacity: 0; transform: translateX(-44px); transition: opacity 0.8s ease, transform 0.8s cubic-bezier(.16,1,.3,1); }
        .split-anim-left.in  { opacity: 1; transform: translateX(0); }
        .split-anim-right { opacity: 0; transform: translateX(44px); transition: opacity 0.8s ease 0.1s, transform 0.8s cubic-bezier(.16,1,.3,1) 0.1s; }
        .split-anim-right.in { opacity: 1; transform: translateX(0); }
        .section-fade { opacity: 0; transform: translateY(30px); transition: opacity 0.75s ease, transform 0.75s cubic-bezier(.16,1,.3,1); }
        .section-fade.in { opacity: 1; transform: translateY(0); }
        .form-input:focus { border-color: #c1348b !important; box-shadow: 0 0 0 3px rgba(193,52,139,0.12) !important; }
        .form-input::placeholder { color: #cbd5e1; }
        .d0 { transition-delay: 0s !important; } .d05 { transition-delay: 0.05s !important; }
        .d1s { transition-delay: 0.1s !important; } .d15 { transition-delay: 0.15s !important; }
        .d2s { transition-delay: 0.2s !important; } .d25 { transition-delay: 0.25s !important; }
        .d3s { transition-delay: 0.3s !important; } .d35 { transition-delay: 0.35s !important; }
        .d4s { transition-delay: 0.4s !important; } .d5s { transition-delay: 0.5s !important; }
        @keyframes progressBar { from { width: 0; } to { width: 100%; } }

        /* ── RESPONSIVE ── */
        @media (max-width: 900px) {
          .hero-content-wrap { padding: 0 24px !important; }
          .about-layout { flex-direction: column !important; }
          .about-text-col { max-width: 100% !important; }
          .stats-row { flex-direction: column !important; gap: 12px !important; }
          .about-imgs-col { min-height: 320px !important; }
        }
        @media (max-width: 600px) {
          .hero-cta-row { flex-direction: column !important; align-items: flex-start !important; }
        }
      `}</style>

            {/* ─── INTRO ANIMATION: ESTILO ASTON MARTIN (PALETA CO MARCA) ─── */}
            <BrandIntroAnimation />

            {/* ─── BANNER HERO: ASTON MARTIN FULLSCREEN INTERACTIVO ─── */}
            <AstonMartinHero
                slides={slides}
                onCotizarClick={() => setIsCotizacionOpen(true)}
                advisorWhatsappUrl="https://wa.me/59176204601?text=Hola%20Co%20Marca,%20deseo%20m%C3%A1s%20informaci%C3%B3n%20sobre%20sus%20espacios"
            />

            {/* ABOUT US */}
            <section
                ref={aboutRef as React.RefObject<HTMLDivElement | null>}
                className={`fade-up-anim ${aboutInView ? 'in' : ''} about-us-section`}
                style={{ padding: '80px 32px 36px', background: isDark ? '#0e071a' : '#fff', position: 'relative' }}
            >
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    {/* TÍTULO ARRIBA */}
                    <div style={{ marginBottom: '36px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                            <span style={{ fontSize: '0.82rem', fontWeight: '800', letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.secondary }}>
                                Sobre Nosotros
                            </span>
                            <div style={{ width: '48px', height: '3px', background: colors.secondary, borderRadius: '3px' }} />
                        </div>
                        <h2 style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 'clamp(2.4rem, 4.5vw, 3.2rem)',
                            fontWeight: '900',
                            color: isDark ? '#ffffff' : '#0f172a',
                            lineHeight: '1.15',
                            letterSpacing: '-0.02em',
                            marginBottom: '24px',
                        }}>
                            Bienvenido a{' '}
                            <span style={{ color: colors.secondary }}>Co Marca</span>
                        </h2>

                        {/* DESCRIPCIÓN Y ESTADÍSTICAS */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                            gap: '36px',
                            alignItems: 'center',
                        }}>
                            <p style={{ fontSize: '1.02rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.8', margin: 0 }}>
                                En La Paz, Bolivia, Co Marca reúne a profesionales, emprendedores y empresas en una comunidad donde las ideas crecen al compartirlas.
                                Aquí encuentras espacios para concentrarte, reunirte con tu equipo y conocer a personas con quienes colaborar y hacer avanzar tus proyectos.
                            </p>
                            <div className="stats-row" style={{ display: 'flex', gap: '16px' }}>
                                {[
                                    { end: 8, suffix: '+', label: 'Años de experiencia' },
                                    { end: 120, suffix: '+', label: 'Proyectos realizados' },
                                    { end: 300, suffix: '+', label: 'Miembros activos' },
                                ].map((s) => (
                                    <div key={s.label} className="stat-card" style={{
                                        flex: 1, background: isDark ? '#150d24' : '#fff',
                                        border: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid rgba(97, 37, 123, 0.15)',
                                        borderTop: `3px solid ${isDark ? '#C1348B' : '#61257B'}`,
                                        borderRadius: '16px', padding: '20px 12px',
                                        boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(97, 37, 123, 0.06)',
                                    }}>
                                        <AnimatedCounter end={s.end} suffix={s.suffix} label={s.label} color={isDark ? '#FFD97C' : '#61257B'} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* IMÁGENES MÁS GRANDES OCUPANDO TODO EL ESPACIO */}
                    <div className="about-imgs-col" style={{
                        width: '100%',
                        maxWidth: '100%',
                        borderRadius: '24px',
                        overflow: 'hidden',
                        boxShadow: '0 20px 60px rgba(97, 37, 123, 0.08)',
                    }}>
                        <AccordionGallery
                            items={spacesList}
                            defaultIndex={1}
                            expandRatio={0.46}
                            trigger="hover"
                            accentColor="#61257B"
                            overlayColor="#130722"
                            textColor="#ffffff"
                            height={520}
                            gap={14}
                            radius={22}
                            grayscale={false}
                            duration={0.4}
                        />
                    </div>
                </div>
            </section>

            <CarouselSection customSlides={experienciaList.length > 0 ? experienciaList : undefined} />
            <ClientsSection />

            {/* SECCIONES DIVIDIDAS (SPLIT SECTIONS) DINÁMICAS O FALLBACK */}
            {splitSectionsList.length > 0 ? (
                splitSectionsList.map((sec) => (
                    <SplitSection
                        key={sec.id}
                        imageLeft={sec.imagen_izquierda}
                        tag={sec.tag}
                        title={sec.titulo}
                        description={sec.descripcion}
                        features={sec.caracteristicas_lista || (sec.caracteristicas ? sec.caracteristicas.split('\n').map(s => s.trim()).filter(Boolean) : [])}
                        accent={sec.color_acento || '#61257B'}
                        bg={isDark ? '#0b0614' : '#fff'}
                        icon=""
                        href={sec.enlace_boton || '/espacios'}
                        buttonText={sec.texto_boton || 'Obtener más información →'}
                        imageSrc={sec.url_imagen || sec.imagen || espacioImg}
                    />
                ))
            ) : (
                <>
                    {/* SECCIÓN 1: IMAGEN IZQ + TEXTO DER (con imagen espacio.jpeg) */}
                    <SplitSection
                        imageLeft={true}
                        tag="Para todos"
                        title="Espacio de trabajo simplificado"
                        description="Independientemente de tu presupuesto o necesidad, hacemos que encontrar el espacio de trabajo perfecto sea fácil. Desde membresías flexibles a oficinas listas para usar, te ofrecemos el espacio y las soluciones para que hagas tu mejor trabajo."
                        features={['Membresías flexibles', 'Oficinas llave en mano', 'Comunidad activa', 'Soporte 24/7']}
                        accent="#61257B"
                        bg={isDark ? '#0b0614' : '#fff'}
                        icon=""
                        href="/espacios/coworking"
                        imageSrc={espacioImg}
                    />

                    {/* SECCIÓN 2: TEXTO IZQ + IMAGEN DER (con imagen solucion.jpeg) */}
                    <SplitSection
                        imageLeft={false}
                        tag="Empresas"
                        title="Soluciones de nivel empresarial para impulsar tu estrategia híbrida"
                        description="Brinda más flexibilidad a tu portafolio de bienes raíces mientras ahorras en costos mediante la combinación de nuestras oficinas llave en mano, espacios de coworking y tecnología de gestión de espacios."
                        features={['Gestión de espacios', 'Oficinas privadas', 'Salas de reuniones', 'Tecnología integrada']}
                        accent="#61257B"
                        bg={isDark ? '#0b0614' : '#fff'}
                        icon=""
                        href="/espacios/empresarial"
                        imageSrc={solucionImg}
                    />
                </>
            )}

            <ContactSection />
            <BlogSection />

            {/* Modal de Cotización y Reserva en Pasos */}
            <CotizacionModal
                isOpen={isCotizacionOpen}
                onClose={() => setIsCotizacionOpen(false)}
            />
        </div>
    );
};

export default LandingPage;
